import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import { HelmetProvider } from 'react-helmet-async'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

import { AuthProvider, useAuth } from './contexts/AuthContext'
import type { User } from '@/types'
import { ThemeProvider } from './contexts/ThemeContext'
import ProtectedRoute from './components/ProtectedRoute'
import ErrorBoundary from './components/ErrorBoundary'

// Lazy load pages for code splitting
const SignInPage = lazy(() => import('./pages/SignInPage'))
const SignUpPage = lazy(() => import('./pages/SignUpPage'))
const WaitlistPage = lazy(() => import('./pages/WaitlistPage'))
const DashboardPage = lazy(() => import('./pages/DashboardPage'))
const PropertiesPage = lazy(() => import('./pages/PropertiesPage'))
const LeadConversationPage = lazy(() => import('./pages/LeadConversationPage'))
// const TenantApplicationsPage = lazy(() => import('./pages/TenantApplicationsPage'))
// const UploadLeasePage = lazy(() => import('./pages/UploadLeasePage'))
const LeasesPage = lazy(() => import('./pages/LeasesPage'))
const PaymentsPage = lazy(() => import('./pages/PaymentsPage'))
// const ShowingsPage = lazy(() => import('./pages/ShowingsPage'))
const ScreeningRequestPage = lazy(() => import('./pages/ScreeningRequestPage'))
const SettingsPage = lazy(() => import('./pages/SettingsPage'))
const AgentsPage = lazy(() => import('./pages/AgentsPage'))
const AssistantPage = lazy(() => import('./pages/AssistantPage'))
const ContactHistoryPage = lazy(() => import('./pages/ContactHistoryPage'))
const WorkflowApplicationsPage = lazy(() => import('./pages/WorkflowApplicationsPage'))
const AgentRuntimePage = lazy(() => import('./pages/AgentRuntimePage'))
const IntegrationsPage = lazy(() => import('./pages/IntegrationsPage'))
const TasksPage = lazy(() => import('./pages/TasksPage'))
const AuditPage = lazy(() => import('./pages/AuditPage'))
const PipelinePage = lazy(() => import('./pages/PipelinePage'))
const RenewalsPage = lazy(() => import('./pages/RenewalsPage'))
const MaintenancePage = lazy(() => import('./pages/MaintenancePage'))
const InspectionsPage = lazy(() => import('./pages/InspectionsPage'))
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'))

// Loading component - Uses Skeleton for better UX
import { Skeleton } from '@/components/ui/skeleton';

const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-background">
    <div className="flex flex-col items-center space-y-4">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      <Skeleton className="h-4 w-32" />
    </div>
  </div>
)

// Base URL: redirect to dashboard if logged in, waitlist page if waitlist, else signin.
const HomePageWrapper = () => {
  const { user, isAuthenticated, loading } = useAuth() as { user: User | null; isAuthenticated: boolean; loading: boolean }

  if (loading) {
    return <PageLoader />
  }

  if (isAuthenticated) {
    if (user?.status === 'waitlist') {
      return <Navigate to="/waitlist" replace />
    }
    return <Navigate to="/dashboard" replace />
  }

  return <Navigate to="/signin" replace />
}

function App() {
  const LeadsConversationRedirect = () => {
    const location = useLocation()
    return <Navigate to={`/leads${location.search}`} replace />
  }

  return (
    <ErrorBoundary>
      <AuthProvider>
        <ThemeProvider>
          <HelmetProvider>
            <div className="h-screen overflow-hidden bg-background text-foreground">
              <ToastContainer 
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
              />
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* Public routes - Tenant-facing pages (no auth required) */}
                  <Route path="/signin" element={<SignInPage />} />
                  <Route path="/signup" element={<SignUpPage />} />
                  <Route path="/waitlist" element={<WaitlistPage />} />
                  {/* Tenant-facing invite link for rental application (includes showing scheduling) */}
                  <Route path="/apply/:invite_token" element={<ScreeningRequestPage />} />
                  {/* Backwards compatibility for older invite links */}
                  <Route path="/public/showings/:invite_token" element={<ScreeningRequestPage />} />
                  
                  {/* Protected routes - Landlord-facing pages (auth required) */}
                  <Route 
                    path="/dashboard" 
                    element={
                      <ProtectedRoute>
                        <DashboardPage />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/properties" 
                    element={
                      <ProtectedRoute>
                        <PropertiesPage />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/leads"
                    element={
                      <ProtectedRoute>
                        <LeadConversationPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/applications"
                    element={
                      <ProtectedRoute>
                        <WorkflowApplicationsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/tasks"
                    element={
                      <ProtectedRoute>
                        <TasksPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/audit"
                    element={
                      <ProtectedRoute>
                        <AuditPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/leads/conversation"
                    element={
                      <ProtectedRoute>
                        <LeadsConversationRedirect />
                      </ProtectedRoute>
                    }
                  />
                  {/* Tenant Applications - hidden for now */}
                  {/* <Route 
                    path="/tenants" 
                    element={
                      <ProtectedRoute>
                        <TenantApplicationsPage />
                      </ProtectedRoute>
                    } 
                  /> */}
                  {/* Upload Lease - hidden for now */}
                  {/* <Route 
                    path="/upload-lease" 
                    element={
                      <ProtectedRoute>
                        <UploadLeasePage />
                      </ProtectedRoute>
                    } 
                  /> */}
                  <Route 
                    path="/leases" 
                    element={
                      <ProtectedRoute>
                        <LeasesPage />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/payments" 
                    element={
                      <ProtectedRoute>
                        <PaymentsPage />
                      </ProtectedRoute>
                    } 
                  />
                  {/* Showings - hidden for now */}
                  {/* <Route 
                    path="/showings" 
                    element={
                      <ProtectedRoute>
                        <ShowingsPage />
                      </ProtectedRoute>
                    } 
                  /> */}
                  <Route 
                    path="/settings" 
                    element={
                      <ProtectedRoute>
                        <SettingsPage />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/integrations/email" 
                    element={
                      <ProtectedRoute>
                        <Navigate to="/settings?tab=email-ingestion" replace />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/agents" 
                    element={
                      <ProtectedRoute>
                        <AgentsPage />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/assistant" 
                    element={
                      <ProtectedRoute>
                        <AssistantPage />
                      </ProtectedRoute>
                    } 
                  />
                  <Route
                    path="/tickets"
                    element={
                      <ProtectedRoute>
                        <Navigate to="/leads?inbox=tickets" replace />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/contact-history"
                    element={
                      <ProtectedRoute>
                        <ContactHistoryPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/runtime-inspection"
                    element={
                      <ProtectedRoute>
                        <AgentRuntimePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/integrations"
                    element={
                      <ProtectedRoute>
                        <IntegrationsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/pipeline"
                    element={
                      <ProtectedRoute>
                        <PipelinePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/renewals"
                    element={
                      <ProtectedRoute>
                        <RenewalsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/maintenance"
                    element={
                      <ProtectedRoute>
                        <MaintenancePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/inspections"
                    element={
                      <ProtectedRoute>
                        <InspectionsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/analytics"
                    element={
                      <ProtectedRoute>
                        <AnalyticsPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Base URL: redirect by auth state (dashboard / waitlist / signin) */}
                  <Route path="/" element={<HomePageWrapper />} />
                  {/* Catch-all: send to home so HomePageWrapper decides */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Suspense>
            </div>
          </HelmetProvider>
        </ThemeProvider>
      </AuthProvider>
    </ErrorBoundary>
  )
}

export default App
